import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';

// 讓 App 外殼永遠精準貼合「扣掉鍵盤之後看得到的那塊」，並且**不造成 React 重新渲染**。
//
// ─────────────────────────────────────────────────────────────────────────────
// 【現況】兩條路，設計前提剛好相反 —— 不要把其中一條的做法套到另一條
// ─────────────────────────────────────────────────────────────────────────────
//
// A. web（含 iOS Safari / Chrome）與 Android 原生 → visualViewport，**刻意不加 transition**
//    visual viewport 本身就是被瀏覽器連續平移＋縮放的（已由 user 實機回報證實：手指拖曳時
//    畫面連續跟著跑＝事件是連續發的）。補 transition 反而有害——height 慢一截才抵達、
//    top 卻是即時的，兩者在動畫期間不同步 → **整個外殼含最上方 header 一起浮動**
//    （user 回報：「連 header 跟小鈴鐺那一層也跟著從下方向上移動」）。
//
// B. iOS 原生 → keyboardWillShow/WillHide，**必須加 transition**
//    這裡事件「只發一次」（動畫起點送一次，附最終鍵盤高度），中間過程沒有任何事件，
//    所以非得靠 transition 把起點到終點之間補起來不可。
//    ⚠️ 兩條路的前提是相反的：A 是事件連續、補動畫會失準；B 是事件單發、不補就是瞬間到位。
//
// 【iOS 那 0.5 秒延遲的真因（2026-08-20 讀 @capacitor/keyboard 原始碼查出）】
// 先前這裡的註解寫「resize:native 會拿系統的鍵盤秒數與曲線去*動畫*縮放 WebView frame」，
// **那是錯的**。node_modules/@capacitor/keyboard/ios/Sources/KeyboardPlugin/Keyboard.m
// 的 onKeyboardWillShow 實際做的是：
//     double duration = [...UIKeyboardAnimationDurationUserInfoKey...] + 0.2;
//     [self setKeyboardHeight:height delay:duration];   // → performSelector afterDelay
// 也就是**等 (鍵盤動畫秒數 + 0.2) 之後，才「瞬間」setFrame**，全程沒有 UIView 動畫。
// 系統鍵盤動畫是 0.25 秒 → 0.25 + 0.2 = 0.45 秒，正是 user 回報的「跳出後 0.5 秒才跳上來」。
// 所以 iOS 這條路不是「原生已經幫我們動畫好了」，而是「原生刻意延後再瞬間到位」，
// 網頁層必須自己在動畫**起點**就開始移動，才看得到跟著鍵盤走。
//
// 【iOS 分支的三個關鍵細節（皆已用瀏覽器實測驗證，見 ACTION_LOG 2026-08-20）】
// 1. 只改 height、不碰 top → header 完全不動（實測 headerTop 全程 0），滿足
//    user 的「唯一要移動的應該是對話內容那一層」。
// 2. 外殼要加 transform:translateZ(0)。家長端對話室是 ParentLayout 的 position:fixed
//    覆蓋層（overlayMotionStyle），fixed 的定位基準是 viewport **不是**外殼 →
//    只改外殼 height，對話室**完全不會動**（實測：外殼 940→640，覆蓋層仍是 940）。
//    給外殼一個 transform 就讓它成為 fixed 子孫的 containing block，覆蓋層才跟著縮。
//    外殼沒有 border，padding box ＝ border box，所以鍵盤沒開時的幾何與現況完全相同。
// 3. 掛載時要先把 height 從 auto 換成具體 px。CSS transition **無法從 auto 內插**，
//    少了這一步第一次開鍵盤只會瞬間到位（實測：未 seed 時全程只有 1 個值）。
//
// 【為什麼保留 capacitor.config 的 resize:native】
// 它是兜底：萬一外掛沒掛上／事件沒進來，原生仍會在 0.45 秒把 WebView 縮好，
// 輸入列至少不會被鍵盤蓋住（功能面不破，只是回到現在這個慢半拍的樣子）。
// 外殼的 height 是絕對 px，所以那次延遲的 setFrame 落地時外殼尺寸已經一樣＝視覺上無事發生。
//
// 【為什麼樣式一律同步寫、不用 requestAnimationFrame】
// 追平移是延遲敏感的：rAF 會把寫入延到下一個影格，等於永遠慢瀏覽器一格，
// 在鍵盤動畫期間就是持續可見的位移。這裡只寫兩個 style 屬性、且不讀取任何元素幾何
// （vv.height/offsetTop 是視窗屬性，不觸發同步 layout），同步寫入沒有 thrash 疑慮。
//
// 【Android 仍未解 — 見 FEATURE_BACKLOG BL-003】
// 「內容跟著鍵盤一起滑上來」在 Android 還沒做到（內容瞬間到位、鍵盤自己滑）。
// 走過的路與結論，留給之後接手的人（以下三條**全部是 Android 的**，別套到 iOS）：
//   - Android 的 adjustResize 是**把 WebView 本身瞬間縮小**，網頁畫不到 WebView 邊界之外，
//     所以這個瞬間到位在網頁層無論如何補不了動畫。
//   - 試過改 manifest `adjustNothing`（視窗不縮、鍵盤蓋上來）＋監聽外掛的 keyboardWillShow
//     自己推內容。**實機結果是畫面完全不動** → 該模式下外掛的 WindowInsetsAnimation
//     callback 沒有送到（推測 adjustNothing 之下 IME insets 不會派發給視窗），
//     且輸入框會被鍵盤蓋住＝功能面破掉，故已退回 adjustResize。
//     若要再試，方向是先讓 MainActivity 走 edge-to-edge（setDecorFitsSystemWindows(false)）
//     讓 IME insets 真的派發，再驗證 keyboardWillShow 有沒有進來。
//   - 另外查證確認：@capacitor/keyboard 的 **Android 端根本沒讀 `resize` 設定**
//     （Keyboard.java 只讀 resizeOnFullScreen）→ `resize:native` 是 iOS 專用。
//
// 【data-keyboard】
// 在 <html> 標記目前是否正在打字，讓需要讓位的元件（底部導航列）用純 CSS 反應，零 re-render。
// 用「是否 focus 在可輸入元素」判斷而非高度差，跨平台都準。

const SHELL_LOCK_CLASS = 'app-shell-locked';

// iOS 系統鍵盤的動畫參數。**寫死是有原因的**：這版 @capacitor/keyboard(6.0.4) 的
// keyboardWillShow payload 只有 { keyboardHeight }，沒有動畫秒數（已讀 Keyboard.m 確認，
// notifyListeners 只塞這一個 key），拿不到就只能用系統的標準值。
// 0.25s＋這條曲線是 iOS 鍵盤 curve 的既有近似值，本專案先前的 ANIM_DEFAULT 就是它。
const ANIM_IOS = 'height 250ms cubic-bezier(0.38, 0.7, 0.125, 1)';

const isEditable = (el) => {
    if (!el) return false;
    const tag = el.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable === true;
};

// 從手指按下的元素往上找，看這串祖先裡有沒有「真的可以捲」的元素
// （既宣告了 auto/scroll，內容也真的超出可視範圍）。兩個軸都要看：有些清單是橫向捲的。
const hasScrollableAncestor = (start, boundary) => {
    let el = start instanceof Element ? start : null;
    while (el) {
        const style = window.getComputedStyle(el);
        const canY = (style.overflowY === 'auto' || style.overflowY === 'scroll')
            && el.scrollHeight > el.clientHeight;
        const canX = (style.overflowX === 'auto' || style.overflowX === 'scroll')
            && el.scrollWidth > el.clientWidth;
        if (canY || canX) return true;
        if (el === boundary) break;
        el = el.parentElement;
    }
    return false;
};

export default function useKeyboardViewport(shellRef) {
    useEffect(() => {
        const el = shellRef.current;
        if (!el) return undefined;

        const native = Capacitor.isNativePlatform();
        const platform = Capacitor.getPlatform();
        const isIosNative = native && platform === 'ios';

        document.documentElement.classList.add(SHELL_LOCK_CLASS);

        // iOS 鍵盤上方的輸入輔助列（Done／上一個／下一個）關掉，比照 LINE（user 指定）。
        // 只有 iOS 有這條列；Android 會回 unimplemented，故一律接住。
        if (native) Keyboard.setAccessoryBarVisible({ isVisible: false }).catch(() => {});

        const cleanups = [];

        // ── 手勢：不該捲的地方就別讓瀏覽器拿去平移 visual viewport ──
        let allowGesture = true;
        const onTouchStart = (e) => {
            // 多指（縮放等）一律放行，不要干擾
            allowGesture = e.touches.length > 1 || hasScrollableAncestor(e.target, el);
        };
        const onTouchMove = (e) => {
            if (!allowGesture && e.cancelable) e.preventDefault();
        };
        el.addEventListener('touchstart', onTouchStart, { passive: true });
        el.addEventListener('touchmove', onTouchMove, { passive: false }); // 要能 preventDefault
        cleanups.push(() => {
            el.removeEventListener('touchstart', onTouchStart);
            el.removeEventListener('touchmove', onTouchMove);
        });

        // ── 打字旗標（給導航列讓位用）──
        const setKeyboardFlag = (open) => {
            document.documentElement.dataset.keyboard = open ? 'open' : 'closed';
        };
        const onFocusIn = (e) => { if (isEditable(e.target)) setKeyboardFlag(true); };
        const onFocusOut = () => {
            // focusout 先於下一個 focusin 觸發；延後一格再判斷，避免在兩個輸入框之間
            // 切換時導航列閃現一下（鍵盤其實從頭到尾沒收起來）。
            setTimeout(() => setKeyboardFlag(isEditable(document.activeElement)), 0);
        };
        document.addEventListener('focusin', onFocusIn);
        document.addEventListener('focusout', onFocusOut);
        cleanups.push(() => {
            document.removeEventListener('focusin', onFocusIn);
            document.removeEventListener('focusout', onFocusOut);
        });

        if (isIosNative) {
            // ── iOS 原生：由 keyboardWillShow/WillHide 驅動，**要**加 transition ──
            // （為什麼與下面那條路相反，見檔頭 A/B 對照）

            // 外殼全高的基準。鍵盤關著時的 innerHeight 就是 WebView 的完整高度；
            // resize:native 會在鍵盤開啟後把 WebView 縮小，那時的 innerHeight 不能拿來當基準。
            let baseHeight = window.innerHeight;
            let kbOpen = false;
            // 確認外掛真的在、我們才接管幾何。沒接管時＝維持現況（原生延遲縮 WebView），
            // 這樣萬一 Podfile/packageClassList 沒同步到而外掛缺席，也不會把輸入列留在鍵盤底下。
            let armed = false;

            const seedShell = () => {
                el.style.transition = '';           // 轉向／兜底重置不該有動畫
                el.style.height = `${baseHeight}px`;
            };

            const onWillShow = (info) => {
                const kb = Number(info && info.keyboardHeight) || 0;
                // 高度不合理就整個不接管，讓 resize:native 照舊處理。
                // 寧可慢半拍，也不要因為算錯而把輸入列留在鍵盤下面（那是功能面破掉）。
                if (kb <= 0 || kb >= baseHeight) return;
                kbOpen = true;
                el.style.transition = ANIM_IOS;
                el.style.height = `${baseHeight - kb}px`;
            };

            const onWillHide = () => {
                kbOpen = false;
                el.style.transition = ANIM_IOS;
                el.style.height = `${baseHeight}px`;
            };

            // 這個 handler 同時是「轉向重新取基準」與「事件沒進來時的兜底自癒」：
            // 若 willShow 沒送到，原生仍會在 0.45 秒縮 WebView，這裡就會把外殼跟著改成新高度，
            // 行為退回與改動前完全一樣（瞬間到位），不會卡在比 viewport 還高的尺寸。
            const onWindowResize = () => {
                if (!armed || kbOpen) return;
                // 收鍵盤時原生幾乎立刻還原 WebView frame（Keyboard.m 用 delay:0.01），
                // 值沒變就別動——否則會把 onWillHide 正在跑的收合動畫打斷成瞬間到位。
                if (window.innerHeight === baseHeight) return;
                baseHeight = window.innerHeight;
                seedShell();
            };
            window.addEventListener('resize', onWindowResize);
            cleanups.push(() => window.removeEventListener('resize', onWindowResize));

            let disposed = false;
            const handles = [];
            cleanups.push(() => {
                disposed = true;
                handles.forEach((h) => h.remove());
                el.style.transform = '';
            });

            const register = (name, fn, onFirstReady) => {
                Keyboard.addListener(name, fn).then((h) => {
                    if (disposed) { h.remove(); return; }
                    handles.push(h);
                    if (onFirstReady) onFirstReady();
                }).catch(() => {});   // 外掛不在就整條不接管，維持現況
            };

            register('keyboardWillShow', onWillShow, () => {
                armed = true;
                baseHeight = window.innerHeight;
                // 讓外殼成為 fixed 子孫的 containing block，家長端對話室（fixed 覆蓋層）才跟得動；
                // 並把 height 從 auto 換成具體 px，transition 才有得內插。兩者的理由見檔頭第 2、3 點。
                el.style.transform = 'translateZ(0)';
                seedShell();
            });
            register('keyboardWillHide', onWillHide);
        } else {
            // ── web 與 Android 原生：由 visualViewport 驅動，不加 transition ──
            // （Android 走 manifest 的 adjustResize，視窗會縮，vv 會跟著回報新高度）
            const vv = window.visualViewport;
            // 不支援 visualViewport → 什麼都不做，外殼維持 CSS 的 top:0/bottom:0 全高
            if (vv) {
                const apply = () => {
                    const height = vv.height;
                    // 防呆：轉向瞬間某些瀏覽器會回報 0 或異常小的值，這時寧可不動，
                    // 讓 CSS 的全高繼續生效，也不要把整個 App 壓成一條線。
                    if (!height || height < 200) return;
                    el.style.height = `${height}px`;
                    el.style.top = `${vv.offsetTop || 0}px`;
                };
                vv.addEventListener('resize', apply);
                vv.addEventListener('scroll', apply);
                apply();
                cleanups.push(() => {
                    vv.removeEventListener('resize', apply);
                    vv.removeEventListener('scroll', apply);
                });

                // 【修導航列卡死，2026-08-31】el.style.height/top 是命令式寫死在 DOM 上的，
                // 只有這個 effect 的 cleanup（外殼 unmount，整個 session 幾乎不會發生）才會清掉。
                // 如果最後一次 apply() 剛好抓到「鍵盤還開著」的較矮高度，接著使用者把 PWA
                // 切到背景（鍵盤被系統收起，但 focusout 不一定會正常觸發、也不會再有
                // 新的 resize/scroll 事件），回到前景時外殼會停在那個較矮的高度——
                // 底部導航（sticky bottom:0）緊貼外殼下緣，看起來就是「卡在螢幕中間、
                // 怎麼滑都不動」，且因為是命令式 style、不是 React state，只有強制關閉
                // App（整個 JS context 被丟掉）才會恢復。
                // 修法：App 回到前景時強制重新讀一次 visualViewport，把外殼校正回真正的
                // 目前高度——不管上次卡在哪個值，這裡都會覆寫過去。
                const onVisibilityChange = () => {
                    if (document.visibilityState === 'visible') apply();
                };
                document.addEventListener('visibilitychange', onVisibilityChange);
                cleanups.push(() => document.removeEventListener('visibilitychange', onVisibilityChange));
            }
        }

        return () => {
            cleanups.forEach((fn) => fn());
            document.documentElement.classList.remove(SHELL_LOCK_CLASS);
            delete document.documentElement.dataset.keyboard;
            el.style.transition = '';
            el.style.height = '';
            el.style.top = '';
        };
    }, [shellRef]);
}
